import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { CommentThread } from "./CommentThread";
import { MarkdownView } from "./MarkdownView";
import { RichTextEditor } from "./RichTextEditor";
import { SubtaskList } from "./SubtaskList";
import { DependencySection } from "./DependencySection";
import { CustomFieldsEditor } from "./CustomFieldsEditor";
import { ReactionBar } from "./ReactionBar";
import { TagPicker } from "./TagPicker";
import { AttachmentList } from "./AttachmentList";
import { TaskActivityFeed } from "./TaskActivityFeed";
import { AssigneeCombobox } from "./AssigneeCombobox";
import { parseDateLocal } from "@/lib/format";
import { Icon } from "./Icon";
import { PriorityChip } from "./PriorityChip";
import { Pill, type TaskStatus } from "./Pill";
import { Avatar } from "./Avatar";
import { Skeleton } from "./Skeleton";
import { Button } from "./Button";
import { statusLabel as formatStatusLabel } from "@/lib/projectStatus";
import { useEffortUnit } from "@/lib/effort";

/**
 * S6 Task detail — designs/TaskDetail.html
 *
 * Two-column layout: `.td-main` (title, meta-row, description, subtasks,
 * attachments, comments/activity tabs) on the left and a sticky aside on
 * the right with `.prop-card` (9 property fields) + `.watchers-card`.
 *
 * Top action bar (`.td-topbar`): back link, mono task ID, Watch, Share,
 * Mark complete.
 */

function formatDate(value: string | null | undefined): string {
  const d = parseDateLocal(value);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDueShort(value: string | null | undefined): string | null {
  const d = parseDateLocal(value);
  if (!d) return null;
  const month = d.toLocaleString(undefined, { month: "short" }).toUpperCase();
  return `DUE ${month} ${d.getDate()}`;
}

const STATUS_OPTIONS: { value: TaskStatus; label: string; dot: string }[] = [
  { value: "backlog", label: "Backlog", dot: "#94a0b4" },
  { value: "todo", label: "To Do", dot: "#3b82d4" },
  { value: "doing", label: "Doing", dot: "#e8612c" },
  { value: "done", label: "Done", dot: "#1f8a5b" },
];

const PRIORITY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Urgent" },
  { value: 2, label: "High" },
  { value: 3, label: "Medium" },
  { value: 4, label: "Low" },
];

// ---------------------------------------------------------------------------
// StatusPicker — click the status pill, choose a new status, save.
// ---------------------------------------------------------------------------

function StatusPicker({
  value,
  onChange,
  disabled,
}: {
  value: TaskStatus;
  onChange: (v: TaskStatus) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  return (
    <div className="inline-picker" ref={rootRef}>
      <button
        type="button"
        className="inline-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Pill variant={value as TaskStatus} dot>
          {formatStatusLabel(value)}
        </Pill>
      </button>
      {open && (
        <div className="inline-picker-panel" role="listbox">
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={value === o.value}
              className={`inline-picker-row${value === o.value ? " on" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <span className="dot" aria-hidden style={{ background: o.dot }} />
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PriorityPicker — click the priority chip, choose a new priority, save.
// ---------------------------------------------------------------------------

function PriorityPicker({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  return (
    <div className="inline-picker" ref={rootRef}>
      <button
        type="button"
        className="inline-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <PriorityChip level={value} />
      </button>
      {open && (
        <div className="inline-picker-panel" role="listbox">
          {PRIORITY_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={value === o.value}
              className={`inline-picker-row${value === o.value ? " on" : ""}`}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              <PriorityChip level={o.value} />
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditableTitle — click → input, blur or Enter saves.
// ---------------------------------------------------------------------------

function EditableTitle({
  value,
  onSave,
}: {
  value: string;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  if (!editing) {
    return (
      <h1
        className="td-title editable"
        tabIndex={0}
        role="button"
        title="Click to edit title"
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        {value}
      </h1>
    );
  }
  function commit() {
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    setEditing(false);
  }
  return (
    <input
      className="td-title editable td-title-input"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          setDraft(value);
          setEditing(false);
        }
      }}
      aria-label="Task title"
    />
  );
}

// ---------------------------------------------------------------------------
// EditableDescription — click → textarea, blur saves. Display via MarkdownView.
// ---------------------------------------------------------------------------

function EditableDescription({
  value,
  taskId,
  onSave,
}: {
  value: string | null | undefined;
  taskId: string;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);
  if (!editing) {
    return (
      <div
        className="desc editable"
        role="button"
        tabIndex={0}
        title="Click to edit description"
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        {value ? (
          <MarkdownView source={value} />
        ) : (
          <p className="t-sm t-muted">No description yet. Click to add one.</p>
        )}
      </div>
    );
  }
  function commit() {
    if ((draft ?? "") !== (value ?? "")) onSave(draft);
    setEditing(false);
  }
  // RichTextEditor's toolbar steals focus from the textarea, so blur-to-save
  // would fire on every format click. Use explicit Save / Cancel instead.
  return (
    <div className="desc-edit">
      <RichTextEditor
        value={draft}
        onChange={setDraft}
        ariaLabel="Task description"
        upload={{ taskId }}
        minRows={5}
        autoFocus
        placeholder="Add detail, context, links… paste an image to attach it"
      />
      <div className="desc-edit-actions">
        <Button variant="primary" size="sm" onClick={commit}>
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft(value ?? "");
            setEditing(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// LinkedDocuments — docs attached to this task (Collaborative documents). Opens
// each in the project Docs view via the ?doc= deep-link.
function LinkedDocuments({
  taskId,
  projectId,
}: {
  taskId: string;
  projectId: string;
}) {
  const q = useQuery({
    queryKey: ["tasks", taskId, "documents"],
    queryFn: () => api.listTaskDocuments(taskId),
  });
  const docs = q.data?.documents ?? [];
  if (docs.length === 0) return null;
  return (
    <section className="blk" aria-label="Documents">
      <h3>Documents</h3>
      <ul className="linked-docs">
        {docs.map((d) => (
          <li key={d.id}>
            <Link
              to="/projects/$projectId/docs"
              params={{ projectId }}
              search={{ doc: d.id }}
              className="adr-link"
            >
              {d.title}
            </Link>
            <span className="t-sm t-muted"> · {d.kind}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TaskDetail({ taskId }: { taskId: string }) {
  const qc = useQueryClient();
  // Comments / Activity tab strip — matches docs/themes/default/TaskDetail.html
  // .tabs2 / .tab2 markup. Activity tab pulls from GET /api/tasks/:taskId/activity.
  const [activeTab, setActiveTab] = useState<"comments" | "activity">(
    "comments",
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.task(taskId),
    queryFn: () => api.getTask(taskId),
  });

  // Current user — needed for isWatching check and the composer avatar.
  const { data: meData } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => api.me(),
  });

  // Fetch project name for the property rail (client-side lookup).
  const projectId = data?.task.projectId;
  const { data: projectData } = useQuery({
    queryKey: queryKeys.project(projectId ?? ""),
    queryFn: () => api.getProject(projectId!),
    enabled: !!projectId,
  });
  const milestonesQ = useQuery({
    queryKey: queryKeys.milestonesFor(projectId ?? ""),
    queryFn: () => api.listMilestones(projectId!),
    enabled: !!projectId,
  });
  const epicsQ = useQuery({
    queryKey: queryKeys.epicsFor(projectId ?? ""),
    queryFn: () => api.listEpics(projectId!),
    enabled: !!projectId,
  });

  // Team list — drives the assignee combobox and the reporter-name lookup (T34).
  const team = useQuery({
    queryKey: queryKeys.team(),
    queryFn: api.listTeam,
  });

  // Watch / unwatch / complete + general patch mutations.
  const watch = useMutation({
    mutationFn: () => api.watchTask(taskId),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const unwatch = useMutation({
    mutationFn: () => api.unwatchTask(taskId),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const markComplete = useMutation({
    mutationFn: () => api.patchTask(taskId, { status: "done" }),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchTask>[1]) =>
      api.patchTask(taskId, body),
    onSuccess: () => invalidateForTask(qc, { projectId, taskId }),
  });
  const effortUnit = useEffortUnit();

  if (isLoading) {
    return (
      <div className="pg-wrap" style={{ padding: "22px 30px 80px" }}>
        <Skeleton width={300} height={28} />
        <Skeleton height={140} style={{ marginTop: 16 }} />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="pg-wrap" style={{ padding: 30 }}>
        <p className="t-sm t-muted">
          Couldn't load this task. Try again in a moment.
        </p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="pg-wrap" style={{ padding: 30 }}>
        <p className="t-sm t-muted">Task not found.</p>
      </div>
    );
  }

  const t = data.task;

  // Assignee display — prefer assigneeIds array, fall back to legacy assigneeId.
  const assigneeList: string[] = t.assigneeIds?.length
    ? t.assigneeIds
    : t.assigneeId
      ? [t.assigneeId]
      : [];

  const members = team.data?.members ?? [];
  const usersById = new Map(
    members.map((m) => [m.id, m.name || m.email || m.id]),
  );
  const imagesById = new Map(members.map((m) => [m.id, m.image ?? null]));

  const projectName = projectData?.project?.name ?? t.projectId;

  // T34: resolve createdBy to a display name via the team list.
  const reporterId = t.createdBy ?? null;
  const reporterName = reporterId
    ? (usersById.get(reporterId) ?? reporterId)
    : null;

  // Watcher state — TaskRow exposes watcherIds on the detail payload.
  const watcherIds: string[] = t.watcherIds ?? [];
  const meId = meData?.user?.id;
  const isWatching = meId ? watcherIds.includes(meId) : false;
  const meName = meData?.user?.name ?? meData?.user?.email ?? "Me";

  const dueShort = formatDueShort(t.dueDate);
  const statusLabel = formatStatusLabel(t.status);

  return (
    <article
      className="task-detail pg-wrap"
      style={{ padding: "22px 30px 80px", maxWidth: 1140 }}
    >
      {/* ── Top action bar ── */}
      <div className="td-topbar">
        <Link
          to="/projects/$projectId"
          params={{ projectId: t.projectId }}
          className="btn btn-secondary btn-sm"
          style={{ textDecoration: "none" }}
        >
          <Icon
            name="arrow"
            size={14}
            style={{ transform: "rotate(180deg)" }}
          />
          Board
        </Link>
        <span className="id">#{t.id.slice(0, 8)}</span>
        <span className="sp" />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (isWatching ? unwatch.mutate() : watch.mutate())}
          disabled={watch.isPending || unwatch.isPending}
        >
          <Icon name={isWatching ? "check" : "star"} size={14} />
          {isWatching ? "Watching" : "Watch"}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            void navigator.clipboard.writeText(window.location.href)
          }
        >
          <Icon name="clip" size={14} />
          Share
        </Button>
        {!t.parentTaskId && (
          <MoveToProjectControl
            taskId={taskId}
            currentProjectId={t.projectId}
          />
        )}
        {t.status !== "done" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => markComplete.mutate()}
            disabled={markComplete.isPending}
          >
            <Icon name="check" size={14} />
            Mark complete
          </Button>
        )}
      </div>

      <div className="td-grid">
        {/* ── Left: main content ── */}
        <div className="td-main">
          <EditableTitle
            value={t.title}
            onSave={(v) => patch.mutate({ title: v })}
          />

          <div className="td-metarow">
            <Pill variant={t.status as TaskStatus} dot>
              {statusLabel}
            </Pill>
            {t.isBlocked && (
              <span className="blocked-badge" title="Has an unfinished blocker">
                <Icon name="flag" size={12} /> Blocked
              </span>
            )}
            <PriorityChip level={t.priority} />
            {(t.tags ?? []).map((tag) => (
              <span key={tag.id} className="tag">
                <span className="tdot" style={{ background: tag.color }} />
                {tag.name}
              </span>
            ))}
            {dueShort && <span className="t-mono due">{dueShort}</span>}
          </div>

          <ReactionBar targetType="task" targetId={taskId} />

          {/* ── Description ── */}
          <section className="blk" aria-label="Description">
            <h3>Description</h3>
            <EditableDescription
              value={t.description}
              taskId={taskId}
              onSave={(v) => patch.mutate({ description: v })}
            />
            <div style={{ marginTop: 12 }}>
              <TagPicker
                taskId={taskId}
                projectId={t.projectId}
                taskTags={t.tags ?? []}
              />
            </div>
          </section>

          <LinkedDocuments taskId={taskId} projectId={t.projectId} />

          {/* ── Bug details (only when typed as a Bug) ── */}
          {t.type === "bug" && (
            <section className="blk" aria-label="Bug details">
              <h3>Bug details</h3>
              <div className="bug-grid">
                <label className="bug-field">
                  <span className="bug-label">Severity</span>
                  <select
                    className="input"
                    value={t.severity ?? ""}
                    onChange={(e) =>
                      patch.mutate({
                        severity: (e.target.value || null) as
                          | "trivial"
                          | "minor"
                          | "major"
                          | "critical"
                          | null,
                      })
                    }
                    aria-label="Severity"
                  >
                    <option value="">—</option>
                    <option value="trivial">Trivial</option>
                    <option value="minor">Minor</option>
                    <option value="major">Major</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label className="bug-field">
                  <span className="bug-label">Environment</span>
                  <input
                    className="input"
                    defaultValue={t.environment ?? ""}
                    placeholder="e.g. iOS 17, Chrome 124"
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v !== (t.environment ?? ""))
                        patch.mutate({ environment: v || null });
                    }}
                    aria-label="Environment"
                  />
                </label>
              </div>
              <label className="bug-field" style={{ marginTop: 10 }}>
                <span className="bug-label">Steps to reproduce</span>
                <textarea
                  className="input"
                  rows={4}
                  defaultValue={t.reproSteps ?? ""}
                  placeholder={"1. …\n2. …\n3. …"}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v !== (t.reproSteps ?? ""))
                      patch.mutate({ reproSteps: v || null });
                  }}
                  aria-label="Steps to reproduce"
                />
              </label>
            </section>
          )}

          {/* ── Subtasks (only on parent tasks) ── */}
          {!t.parentTaskId && (
            <SubtaskList taskId={taskId} projectId={t.projectId} />
          )}

          {/* ── Dependencies (blocks / blocked-by) ── */}
          <DependencySection taskId={taskId} projectId={t.projectId} />

          {/* ── Custom fields (per-project, user-defined) ── */}
          <CustomFieldsEditor taskId={taskId} projectId={t.projectId} />

          {/* ── Attachments ── */}
          <AttachmentList
            taskId={taskId}
            projectId={t.projectId}
            ownerId={projectData?.project?.ownerId}
          />

          {/* ── Comments / Activity tabs ── */}
          <section className="blk" aria-label="Discussion">
            <div className="tabs2" role="tablist" aria-label="Task discussion">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "comments"}
                className={`tab2 ${activeTab === "comments" ? "on" : ""}`}
                onClick={() => setActiveTab("comments")}
              >
                Comments
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "activity"}
                className={`tab2 ${activeTab === "activity" ? "on" : ""}`}
                onClick={() => setActiveTab("activity")}
              >
                Activity
              </button>
            </div>
            {activeTab === "comments" ? (
              <CommentThread
                taskId={taskId}
                projectId={t.projectId}
                meId={meId}
                meName={meName}
              />
            ) : (
              <TaskActivityFeed taskId={taskId} />
            )}
          </section>
        </div>

        {/* ── Right: property rail + watchers ── */}
        <aside className="td-aside">
          <dl className="prop-card">
            <div className="prop">
              <dt className="pl">
                <Icon name="board" size={14} />
                Status
              </dt>
              <dd className="pv">
                <StatusPicker
                  value={t.status as TaskStatus}
                  onChange={(v) => patch.mutate({ status: v })}
                  disabled={patch.isPending}
                />
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="users" size={14} />
                Assignee
              </dt>
              <dd className="pv">
                <AssigneeCombobox
                  members={members}
                  loading={team.isLoading}
                  value={assigneeList[0] ?? null}
                  onChange={(id) => patch.mutate({ assigneeId: id })}
                />
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="flag" size={14} />
                Priority
              </dt>
              <dd className="pv">
                <PriorityPicker
                  value={t.priority}
                  onChange={(v) => patch.mutate({ priority: v })}
                  disabled={patch.isPending}
                />
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="flag" size={14} />
                Milestone
              </dt>
              <dd className="pv">
                <select
                  className="input"
                  style={{ maxWidth: "11rem" }}
                  value={t.milestoneId ?? ""}
                  onChange={(e) =>
                    patch.mutate({ milestoneId: e.target.value || null })
                  }
                  aria-label="Milestone"
                >
                  <option value="">No milestone</option>
                  {(milestonesQ.data?.milestones ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="grid" size={14} />
                Epic
              </dt>
              <dd className="pv">
                <select
                  className="input"
                  style={{ maxWidth: "11rem" }}
                  value={t.epicId ?? ""}
                  onChange={(e) =>
                    patch.mutate({ epicId: e.target.value || null })
                  }
                  aria-label="Epic"
                >
                  <option value="">No epic</option>
                  {(epicsQ.data?.epics ?? []).map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {ep.name}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="grid" size={14} />
                Type
              </dt>
              <dd className="pv">
                <select
                  className="input"
                  style={{ maxWidth: "9rem" }}
                  value={t.type ?? "task"}
                  onChange={(e) =>
                    patch.mutate({
                      type: e.target.value as "task" | "story" | "bug",
                    })
                  }
                  aria-label="Work item type"
                >
                  <option value="task">Task</option>
                  <option value="story">Story</option>
                  <option value="bug">Bug</option>
                </select>
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="cal" size={14} />
                Start date
              </dt>
              <dd className="pv">
                <input
                  type="date"
                  className="due-date-input"
                  value={t.startDate ?? ""}
                  max={t.dueDate ?? undefined}
                  onChange={(e) =>
                    patch.mutate({ startDate: e.target.value || null })
                  }
                  aria-label="Start date"
                />
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="cal" size={14} />
                Due date
              </dt>
              <dd
                className="pv"
                style={t.dueDate ? { color: "var(--orange-600)" } : undefined}
              >
                <input
                  type="date"
                  className="due-date-input"
                  value={t.dueDate ?? ""}
                  min={t.startDate ?? undefined}
                  onChange={(e) =>
                    patch.mutate({ dueDate: e.target.value || null })
                  }
                  aria-label="Due date"
                />
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="clock" size={14} />
                Estimate
              </dt>
              <dd className="pv">
                <input
                  type="number"
                  className="due-date-input"
                  min="0"
                  step="0.5"
                  inputMode="decimal"
                  style={{ width: "5rem" }}
                  value={t.estimate ?? ""}
                  onChange={(e) =>
                    patch.mutate({
                      estimate:
                        e.target.value.trim() === ""
                          ? null
                          : Number(e.target.value),
                    })
                  }
                  aria-label="Estimate"
                />
                <span className="t-muted" style={{ marginLeft: 6 }}>
                  {effortUnit}
                </span>
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="folder" size={14} />
                Project
              </dt>
              <dd className="pv">
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: t.projectId }}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {projectName}
                </Link>
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="star" size={14} />
                Tags
              </dt>
              <dd className="pv">
                {(t.tags ?? []).length > 0 ? (
                  (t.tags ?? []).map((tag) => (
                    <span key={tag.id} className="tag">
                      <span
                        className="tdot"
                        style={{ background: tag.color }}
                      />
                      {tag.name}
                    </span>
                  ))
                ) : (
                  <span className="t-muted">—</span>
                )}
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="users" size={14} />
                Reporter
              </dt>
              <dd className="pv dim">
                {reporterId ? (
                  <>
                    <Avatar
                      id={reporterId}
                      name={reporterName ?? reporterId}
                      size="xs"
                      image={imagesById.get(reporterId)}
                    />
                    {reporterName ?? reporterId}
                  </>
                ) : (
                  <span className="t-muted">—</span>
                )}
              </dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="clock" size={14} />
                Created
              </dt>
              <dd className="pv dim">{formatDate(t.createdAt)}</dd>
            </div>
            <div className="prop">
              <dt className="pl">
                <Icon name="clock" size={14} />
                Updated
              </dt>
              <dd className="pv dim">{formatDate(t.updatedAt)}</dd>
            </div>
          </dl>

          {/* ── Watchers card ── */}
          <div className="watchers-card">
            <div className="watch-row">
              <h4>Watchers · {watcherIds.length}</h4>
              <div className="avatar-group">
                {watcherIds.slice(0, 3).map((id) => (
                  <Avatar
                    key={id}
                    id={id}
                    name={usersById.get(id) ?? id}
                    size="sm"
                    image={imagesById.get(id)}
                  />
                ))}
                <button
                  type="button"
                  className="add-watch"
                  aria-label="Add watcher"
                  onClick={() =>
                    isWatching ? unwatch.mutate() : watch.mutate()
                  }
                  disabled={watch.isPending || unwatch.isPending}
                >
                  <Icon name="plus" size={14} />
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}

/** Owner/member-visible "Move to project" dropdown in the task action bar. */
function MoveToProjectControl({
  taskId,
  currentProjectId,
}: {
  taskId: string;
  currentProjectId: string;
}) {
  const qc = useQueryClient();
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const move = useMutation({
    mutationFn: (projectId: string) => api.moveTask(taskId, projectId),
    onSuccess: (_r, projectId) => {
      // The task now lives in a new project — refresh both boards + the task.
      qc.invalidateQueries({ queryKey: queryKeys.task(taskId) });
      qc.invalidateQueries({ queryKey: queryKeys.tasksFor(currentProjectId) });
      qc.invalidateQueries({ queryKey: queryKeys.tasksFor(projectId) });
      qc.invalidateQueries({ queryKey: queryKeys.projects() });
    },
  });
  const others = (projectsQ.data?.projects ?? []).filter(
    (p) => p.id !== currentProjectId && p.status !== "archived",
  );
  if (others.length === 0) return null;
  return (
    <select
      className="select td-move-select"
      value=""
      disabled={move.isPending}
      onChange={(e) => {
        if (e.target.value) move.mutate(e.target.value);
      }}
      aria-label="Move to project"
      title="Move this task (and its subtasks) to another project"
    >
      <option value="">{move.isPending ? "Moving…" : "Move to…"}</option>
      {others.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  );
}
